import React from 'react';
import { useAppDispatch, useAppSelector } from 'src/store';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import Show from 'src/shared/components/show/Show';
import SnackbarAlert from 'src/shared/components/snackbar-alert/SnackbarAlert';
import Loader from 'src/shared/components/loader/Loader';
import useFiles from 'src/shared/hooks/useFiles';
import useBoolean from 'src/shared/hooks/useBoolean';
import { UploaderType } from 'src/shared/types';
import { Status, statusToAlertColor } from 'src/shared/types';
import UploadFileModal from 'src/app/projects/views/site-files/widgets/UploadFileModal';
import FilesFilters from 'src/app/projects/views/site-files/widgets/FilesFilters';
import FilesTable from 'src/app/projects/views/site-files/widgets/FilesTable';
import EmptyFilesState from 'src/app/projects/views/site-files/widgets/EmptyFilesState';
import usePortfolioFilesAnalytics from './hooks/usePortfolioFilesAnalytics';
import { setSearchText, setUploadedByFilter, getSearchText, getUploadedByFilter } from './state/portfolioFilesSlice';

const PortfolioFiles = () => {
    const dispatch = useAppDispatch();
    const { currentCustomerId } = useCustomer();
    const [isUploadModalOpen, uploadModal] = useBoolean(false);
    const [snackbar, setSnackbar] = React.useState<{ open: boolean; message: string; severity: Status }>({
        open: false,
        message: '',
        severity: Status.SUCCESS,
    });

    const searchText = useAppSelector(getSearchText);
    const uploadedByFilter = useAppSelector(getUploadedByFilter);

    const {
        files: siteFiles = [],
        isRefetching,
        isLoading,
        upload,
        rename,
        removeOne,
        downloadMultipleFiles,
    } = useFiles({ customerId: currentCustomerId || undefined });

    const { trackSingleFileDownload, trackMultipleFilesDownload, trackUploadSuccess, trackUploadError, trackRename, trackDelete } =
        usePortfolioFilesAnalytics();

    const handleShowSnackbar = (message: { message: string; severity: Status }) => {
        setSnackbar({
            open: true,
            ...message,
        });
    };

    const handleUpload = async (files: File[]) => {
        const result = await upload(files);
        if (result.isSuccess) {
            setSnackbar({
                open: true,
                message: result.message,
                severity: Status.SUCCESS,
            });
            trackUploadSuccess({ count: files.length });
            uploadModal.off();
        } else {
            setSnackbar({
                open: true,
                message: 'Failed to upload files. Please try again.',
                severity: Status.ERROR,
            });
            trackUploadError({ message: result.message });
        }
        return result;
    };

    const filters = (
        <FilesFilters
            searchText={searchText}
            onSearchChange={value => dispatch(setSearchText(value))}
            uploadedByFilter={uploadedByFilter}
            onUploadedByChange={vals => {
                dispatch(setUploadedByFilter({ key: UploaderType.NatureMetrics, value: vals.includes(UploaderType.NatureMetrics) }));
                dispatch(setUploadedByFilter({ key: UploaderType.Client, value: vals.includes(UploaderType.Client) }));
            }}
            onUploadClick={uploadModal.on}
        />
    );

    const handleRenameMutation = async (fileId: string, newName: string) => {
        await rename(fileId, newName);
    };

    const handleDeleteMutation = async (fileId: string) => {
        await removeOne(fileId);
    };

    const mainContent = (
        <Show
            when={siteFiles.length > 0}
            fallback={
                isLoading || isRefetching ? (
                    <Loader />
                ) : (
                    <EmptyFilesState onUploadClick={uploadModal.on} textContent='This portfolio has no files yet.' />
                )
            }
        >
            <FilesTable
                files={siteFiles}
                searchText={searchText}
                uploadedByFilter={uploadedByFilter}
                renameFileMutation={handleRenameMutation}
                deleteFileMutation={handleDeleteMutation}
                onShowSnackbar={handleShowSnackbar}
                downloadMultipleFiles={downloadMultipleFiles}
                onSingleDownload={params => trackSingleFileDownload(params)}
                onBulkDownload={params => trackMultipleFilesDownload(params)}
                onRename={params => trackRename(params)}
                onDelete={params => trackDelete(params)}
            />
        </Show>
    );

    return (
        <>
            <StandardAppLayout
                title='Portfolio files'
                subTitle='View portfolio documentation and upload your supporting files'
                mainContent={mainContent}
                filterSection={filters}
            />
            {isUploadModalOpen && (
                <UploadFileModal
                    onClose={uploadModal.off}
                    onUpload={handleUpload}
                    description='Upload portfolio-related documentation which will be accessible to all portfolio users.'
                />
            )}
            <SnackbarAlert
                open={snackbar.open}
                message={snackbar.message}
                severity={statusToAlertColor(snackbar.severity)}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                autoHideDuration={6000}
            />
        </>
    );
};

export default PortfolioFiles;
